import { debug } from 'application/Shared/Utils';

const getAdaSettings = (session = {}, isSignedIn = false) => {
    const handle = process.env.REACT_APP_ADA_HANDLE;
    const testModeEnv = process.env.REACT_APP_ADA_TEST_MODE;
    const testModeDefault = process.env.REACT_APP_ENV !== 'production';
    const testMode =
        typeof testModeEnv === 'string'
            ? testModeEnv === 'true'
            : testModeDefault;

    const firstName = session?.user?.firstName || '';
    const lastName = session?.user?.lastName || '';
    const email = session?.user?.email || '';

    // create or update global ready callback placeholder if not present
    if (typeof window.adaReadyCallback !== 'function') {
        window.adaReadyCallback = () => {
            debug.i('Ada is ready!');
        };
    }

    const metaFields = {
        origin: window.location.origin,
        signed_in: isSignedIn
    };
    if (email) {
        metaFields.email = email;
    }
    if (firstName && lastName) {
        metaFields.name = `${firstName} ${lastName}`.trim();
    }

    return {
        adaReadyCallback: window.adaReadyCallback,
        handle,
        metaFields,
        testMode
    };
};

/**
 * @param {import('application/Shared/types/session').Session | undefined} session user session object (optional, defaults to empty object)
 * @description adds user data to Ada
 * @example
 * addUserDataToAda({
 *     user: {
 *         email: 'test@example.com',
 *         firstName: 'Test',
 *         lastName: 'User'
 *     }
 * });
 *
 * If called with no session object, this will effectively clear the user data from Ada.
 * @returns {void}
 if error adding user data to Ada
 */
const addUserDataToAda = (session = {}, isSignedIn = false) => {
    if (process.env.REACT_APP_ENABLE_ADA === 'true') {
        try {
            const settings = getAdaSettings(session, isSignedIn);
            // ensure global settings reflect latest session
            window.adaSettings = {
                ...(window.adaSettings || {}),
                ...settings,
                metaFields: {
                    ...(window.adaSettings?.metaFields || {}),
                    ...settings.metaFields
                }
            };
            // attempt to update meta fields post-initialization if API exists
            const adaApi = window.AdaEmbed || window.adaEmbed || window.ada;

            if (adaApi && typeof adaApi.setMetaFields === 'function') {
                adaApi.setMetaFields(window.adaSettings.metaFields);
            }
        } catch (e) {
            debug.e('Error adding user properties to Ada', e);
        }
    }
};

const loadAda = (handle) => {
    if (!handle) {
        debug.e('No Ada handle found');
        return;
    }

    const existing = document.getElementById('__ada');
    if (existing) return;

    const script = document.createElement('script');
    script.id = '__ada';
    script.async = true;
    script.setAttribute('data-handle', handle);
    script.src = 'https://static.ada.support/embed2.js';

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
};

// Commenting out in case we need to start/stop Ada later.
// Calling start caused a lot of console errors related to "Ada is already started".
// const startAda = () => {
//     const adaApi = window.AdaEmbed || window.adaEmbed || window.ada;
//     if (adaApi && typeof adaApi.start === 'function') {
//         adaApi.start(window.adaSettings);
//     } else {
//         debug.e('Ada API not found');
//     }
// };

// const stopAda = () => {
//     const adaApi = window.AdaEmbed || window.adaEmbed || window.ada;
//     if (adaApi && typeof adaApi.setMetaFields === 'function') {
//         adaApi.setMetaFields({
//             signed_in: false
//         });
//     }
//     if (adaApi && typeof adaApi.stop === 'function') {
//         adaApi.stop();
//     }
// };

/**
 * @description initializes Ada chat button.
 * @example
 * initializeAda();
 *
 * @returns {void}
 * @throws {Error} if error initializing Ada
 *
 * NOTE: To find any style overrides, search for
 * ```
 * #ada-button-frame
 * #ada-chat-frame
 * #ada-entry
 * ```
 */
const initializeAda = () => {
    const enableAda =
        !document.getElementById('__ada') &&
        process.env.REACT_APP_ENABLE_ADA === 'true';

    if (enableAda) {
        try {
            const handle = process.env.REACT_APP_ADA_HANDLE;
            loadAda(handle);
        } catch (e) {
            debug.e('Error initializing Ada', e);
        }
    }
};

export { initializeAda, addUserDataToAda };
